import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { appService } from '../services/app.service';
import { NoAccessPage } from './NoAccessPage';
import { Loader2 } from 'lucide-react';

interface AppAccessGuardProps {
  appSlug: string;
  children: React.ReactNode;
}

export function AppAccessGuard({ appSlug, children }: AppAccessGuardProps) {
  const { user, currentWorkspace, loading: authLoading, workspaceLoading } = useAuth();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [checkingAccess, setCheckingAccess] = useState(true);


  // Use stable identifiers to prevent unnecessary re-renders
  const userId = user?.uid;
  const workspaceId = currentWorkspace?.id;

  useEffect(() => {
    const checkAccess = async () => {
      // Wait for BOTH auth AND workspace to complete
      if (authLoading || workspaceLoading) return;

      // If no user or workspace after loading, deny access
      if (!user || !workspaceId) {
        setCheckingAccess(false);
        setHasAccess(false);
        return;
      }

      try {
        const idToken = await user.getIdToken();
        const result = await appService.checkAppAccess(
          workspaceId,
          appSlug,
          idToken
        );
        setHasAccess(result.has_access);
      } catch (error) {
        console.error('Failed to check app access:', error);
        setHasAccess(false);
      } finally {
        setCheckingAccess(false);
      }
    };

    // Reset state when dependencies change
    setCheckingAccess(true);
    setHasAccess(null);
    checkAccess();
  }, [userId, workspaceId, appSlug, authLoading, workspaceLoading, user]);

  // Show loading while auth, workspace, or access check in progress
  if (authLoading || workspaceLoading || checkingAccess) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Not authenticated — send to plain /login. We intentionally drop the
  // previous path (no ?redirect=) because, after signing out, the next user
  // signing in on this device shouldn't be sent back to an app-scoped URL
  // they may not have access to (workspace membership differs per user).
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated but no access to this app
  if (!hasAccess) {
    return <NoAccessPage appSlug={appSlug} />;
  }

  // Render children if access granted
  return <>{children}</>;
}
